import React, { useState, useEffect, useRef, useCallback } from 'react';
import lumiObserver from './lumiObserver';

// ── Theme ────────────────────────────────────────────────────────────────
const T = {
  bg: '#0D1117',
  surface: '#161B22',
  border: '#30363D',
  text: '#E6EDF3',
  textMid: '#8B949E',
  accent: '#0B8A7E',
  correct: '#10B981',
  wrong: '#EF4444',
  hint: '#F59E0B',
};

const MAX_ATTEMPTS = 3;

// ── Condition evaluator (sandboxed) ──────────────────────────────────────
function evalHintCondition(cond, state) {
  if (!cond || typeof cond !== 'string') return false;
  try {
    const keys = Object.keys(state);
    const vals = Object.values(state);
    // eslint-disable-next-line no-new-func
    return !!new Function(...keys, `return (${cond});`)(...vals);
  } catch (_) {
    return false;
  }
}

// ── Success evaluator ───────────────────────────────────────────────────
function evalSuccess(cond, state) {
  if (!cond || typeof cond !== 'object') return false;
  const v = state[cond.parameter || 'value'];
  if (cond.type === 'value_equals') {
    return v != null && Math.abs(v - cond.target) <= (cond.tolerance ?? 0);
  }
  if (cond.type === 'value_in_range') {
    return v != null && v >= cond.min && v <= cond.max;
  }
  if (cond.type === 'state_equals') {
    return JSON.stringify(state[cond.key]) === JSON.stringify(cond.target);
  }
  if (cond.type === 'cells_equal') {
    const a = state[cond.key] || [];
    const b = cond.target || [];
    if (a.length !== b.length) return false;
    return a.every((x, i) => x === b[i]);
  }
  return false;
}

// ── Feedback panel ──────────────────────────────────────────────────────
function FeedbackPanel({ status, message, hint, attempts }) {
  // Priority: correct > gave_up > wrong > hint > idle.
  // Hints are shown during idle/wrong status only.
  if (status === 'idle' && !hint) return null;
  const showing = (status === 'correct' || status === 'gave_up' || status === 'wrong')
    ? { kind: status, text: message }
    : { kind: 'hint', text: hint };
  const color = showing.kind === 'correct' ? T.correct
    : showing.kind === 'gave_up' ? T.wrong
    : showing.kind === 'wrong' ? T.wrong
    : T.hint;
  const icon = showing.kind === 'correct' ? '✓'
    : showing.kind === 'gave_up' ? '✗'
    : showing.kind === 'wrong' ? '✗'
    : '💡';
  return (
    <div role="status" aria-live="polite" style={{
      marginTop: 12, padding: '10px 14px', borderRadius: 10,
      background: color + '22', border: `1px solid ${color}`, color,
      fontSize: 13, fontWeight: 600, lineHeight: 1.5,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
    }}>
      <span>{icon} {showing.text}</span>
      {status === 'wrong' && attempts > 0 && (
        <span style={{ opacity: 0.75, fontSize: 11, whiteSpace: 'nowrap' }}>
          {attempts} of {MAX_ATTEMPTS} attempts
        </span>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Each sub-component is a pure view: owns only UI-local state, emits the
// student's current "answer state" on every interaction via onStateChange.
// Attempt counting and success evaluation live in the dispatcher below.
// ══════════════════════════════════════════════════════════════════════════

// ── 1. FRACTION BAR ─────────────────────────────────────────────────────
function FractionBar({ config, onStateChange, disabled, flashCorrect }) {
  const denom = Math.max(2, Math.min(24, config?.denominator ?? 4));
  const [shaded, setShaded] = useState(() => new Array(denom).fill(false));
  useEffect(() => { setShaded(new Array(denom).fill(false)); }, [denom]);

  const toggle = (i) => {
    if (disabled) return;
    const next = [...shaded]; next[i] = !next[i];
    setShaded(next);
    const count = next.filter(Boolean).length;
    onStateChange?.({ value: count / denom, shadedCount: count, denominator: denom });
  };

  const count = shaded.filter(Boolean).length;
  const barW = 360, barH = 60, cellW = barW / denom;

  return (
    <svg width={barW + 20} height={barH + 40} style={{ display: 'block', margin: '0 auto' }}
      role="group" aria-label={`Fraction bar with ${denom} parts, ${count} shaded`}>
      {shaded.map((s, i) => (
        <rect key={i}
          x={10 + i * cellW} y={10}
          width={cellW - 1} height={barH}
          fill={s ? (flashCorrect ? T.correct : T.accent) : T.surface}
          stroke={T.border} strokeWidth={1.5}
          style={{ cursor: disabled ? 'default' : 'pointer', transition: 'fill .15s' }}
          onClick={() => toggle(i)}
          role="button" tabIndex={disabled ? -1 : 0}
          aria-label={`Part ${i + 1} ${s ? 'shaded' : 'unshaded'}`}
          onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(i); } }}
        />
      ))}
      <text x={(barW + 20) / 2} y={barH + 30} textAnchor="middle"
        fill={T.text} fontSize={15} fontWeight={700} fontFamily="'Plus Jakarta Sans',sans-serif">
        {count}/{denom} = {(count / denom).toFixed(3)} = {(100 * count / denom).toFixed(1)}%
      </text>
    </svg>
  );
}

// ── 2. NUMBER LINE ──────────────────────────────────────────────────────
function NumberLine({ config, onStateChange, disabled, flashCorrect }) {
  const min = config?.min ?? 0;
  const max = config?.max ?? 10;
  const step = config?.step ?? 1;
  const snap = config?.snap ?? true;
  const [value, setValue] = useState(config?.initial ?? min);
  const svgRef = useRef(null);
  const drag = useRef(false);

  const W = 420, H = 80, pad = 30;
  const xFor = (v) => pad + ((v - min) / (max - min)) * (W - 2 * pad);
  const valueForX = (x) => {
    let v = min + ((x - pad) / (W - 2 * pad)) * (max - min);
    if (snap) v = Math.round(v / step) * step;
    return Math.max(min, Math.min(max, v));
  };
  const updateFromEvent = (e) => {
    if (disabled) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    if (cx == null) return;
    const v = valueForX(cx - rect.left);
    setValue(v);
    onStateChange?.({ value: v });
  };

  const ticks = [];
  const tickStep = config?.tickStep ?? step;
  for (let t = min; t <= max + 1e-9; t += tickStep) ticks.push(Number(t.toFixed(4)));

  return (
    <svg ref={svgRef} width={W} height={H}
      role="slider" aria-valuemin={min} aria-valuemax={max} aria-valuenow={value}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'ArrowLeft') { const v = Math.max(min, value - step); setValue(v); onStateChange?.({ value: v }); }
        if (e.key === 'ArrowRight') { const v = Math.min(max, value + step); setValue(v); onStateChange?.({ value: v }); }
      }}
      style={{ display: 'block', margin: '0 auto', touchAction: 'none', cursor: disabled ? 'default' : 'grab' }}
      onPointerDown={(e) => { drag.current = true; svgRef.current.setPointerCapture?.(e.pointerId); updateFromEvent(e); }}
      onPointerMove={(e) => { if (drag.current) updateFromEvent(e); }}
      onPointerUp={(e) => { drag.current = false; svgRef.current.releasePointerCapture?.(e.pointerId); }}
      onPointerCancel={() => { drag.current = false; }}
    >
      <line x1={pad} y1={H / 2} x2={W - pad} y2={H / 2} stroke={T.border} strokeWidth={2} />
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={xFor(t)} y1={H / 2 - 6} x2={xFor(t)} y2={H / 2 + 6} stroke={T.textMid} strokeWidth={1.5} />
          <text x={xFor(t)} y={H / 2 + 22} textAnchor="middle" fill={T.textMid} fontSize={10}>{t}</text>
        </g>
      ))}
      <circle cx={xFor(value)} cy={H / 2} r={9}
        fill={flashCorrect ? T.correct : T.accent} stroke="#fff" strokeWidth={2} />
      <text x={xFor(value)} y={H / 2 - 14} textAnchor="middle" fill={T.text} fontSize={13} fontWeight={700}>
        {Number(value).toFixed(step < 1 ? 2 : 0)}
      </text>
    </svg>
  );
}

// ── 3. EQUATION BALANCE ─────────────────────────────────────────────────
function EquationBalance({ config, onStateChange, disabled, flashCorrect }) {
  const weights = config?.weights ?? [1, 5, 10];
  const [left, setLeft] = useState(config?.initialLeft ?? []);
  const [right, setRight] = useState(config?.initialRight ?? (config?.target ? [config.target] : [10]));
  const sumL = left.reduce((a, b) => a + b, 0);
  const sumR = right.reduce((a, b) => a + b, 0);

  useEffect(() => {
    onStateChange?.({
      leftSum: sumL, rightSum: sumR, balanced: sumL === sumR,
      diff: sumL - sumR, value: sumL,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sumL, sumR]);

  const add = (side, w) => {
    if (disabled) return;
    side === 'L' ? setLeft([...left, w]) : setRight([...right, w]);
  };
  const clear = () => {
    if (disabled) return;
    setLeft([]); setRight(config?.initialRight ?? (config?.target ? [config.target] : [10]));
  };

  const diff = sumL - sumR;
  const tilt = Math.max(-15, Math.min(15, diff * 2));
  const cx = 210, cy = 120;
  return (
    <div>
      <svg width={420} height={200} style={{ display: 'block', margin: '0 auto' }} role="img" aria-label={`Balance: left ${sumL}, right ${sumR}`}>
        <polygon points={`${cx - 14},180 ${cx + 14},180 ${cx},${cy + 8}`} fill={T.border} />
        <g transform={`rotate(${tilt} ${cx} ${cy})`}>
          <line x1={cx - 150} y1={cy} x2={cx + 150} y2={cy} stroke={flashCorrect ? T.correct : T.text} strokeWidth={4} strokeLinecap="round" />
          <line x1={cx - 150} y1={cy} x2={cx - 150} y2={cy + 30} stroke={T.textMid} />
          <rect x={cx - 180} y={cy + 30} width={60} height={12} fill={T.surface} stroke={T.accent} />
          <text x={cx - 150} y={cy + 28} textAnchor="middle" fill={T.text} fontSize={14} fontWeight={700}>{sumL}</text>
          <line x1={cx + 150} y1={cy} x2={cx + 150} y2={cy + 30} stroke={T.textMid} />
          <rect x={cx + 120} y={cy + 30} width={60} height={12} fill={T.surface} stroke={T.accent} />
          <text x={cx + 150} y={cy + 28} textAnchor="middle" fill={T.text} fontSize={14} fontWeight={700}>{sumR}</text>
        </g>
      </svg>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        {weights.map(w => (
          <button key={'L' + w} disabled={disabled} onClick={() => add('L', w)} style={btnPrimary(disabled)}>+{w} left</button>
        ))}
        {weights.map(w => (
          <button key={'R' + w} disabled={disabled} onClick={() => add('R', w)} style={btnPrimary(disabled)}>+{w} right</button>
        ))}
        <button onClick={clear} disabled={disabled} style={btnSecondary(disabled)}>Clear</button>
      </div>
    </div>
  );
}

// ── 4. PLACE VALUE BLOCKS ───────────────────────────────────────────────
function PlaceValueBlocks({ config, onStateChange, disabled }) {
  const showThousands = config?.showThousands ?? false;
  const [th, setTh] = useState(config?.initial?.thousands ?? 0);
  const [h, setH] = useState(config?.initial?.hundreds ?? 0);
  const [t, setT] = useState(config?.initial?.tens ?? 0);
  const [o, setO] = useState(config?.initial?.ones ?? 0);

  useEffect(() => {
    const total = th * 1000 + h * 100 + t * 10 + o;
    onStateChange?.({ value: total, thousands: th, hundreds: h, tens: t, ones: o });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [th, h, t, o]);

  const bump = (setter, val, delta) => {
    if (disabled) return;
    setter(Math.max(0, Math.min(20, val + delta)));
  };
  const total = th * 1000 + h * 100 + t * 10 + o;

  const Col = ({ label, val, setter, color, ariaLabel }) => (
    <div style={{ textAlign: 'center', minWidth: 70 }} role="group" aria-label={ariaLabel}>
      <div style={{ fontSize: 11, color: T.textMid, fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minHeight: 140, justifyContent: 'flex-end' }}>
        {[...Array(Math.min(val, 10))].map((_, i) => (<div key={i} style={{ width: 40, height: 10, background: color, borderRadius: 2 }} />))}
        {val > 10 && <div style={{ fontSize: 11, color: T.textMid }}>+{val - 10}</div>}
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginTop: 4 }}>{val}</div>
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 4 }}>
        <button onClick={() => bump(setter, val, -1)} disabled={disabled} aria-label={`Decrease ${label}`} style={btnSmall(disabled)}>−</button>
        <button onClick={() => bump(setter, val, +1)} disabled={disabled} aria-label={`Increase ${label}`} style={btnSmallPrimary(disabled)}>+</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 18 }}>
        {showThousands && <Col label="1000s" val={th} setter={setTh} color="#8B5CF6" ariaLabel="Thousands column" />}
        <Col label="100s" val={h} setter={setH} color="#EF4444" ariaLabel="Hundreds column" />
        <Col label="10s" val={t} setter={setT} color="#F59E0B" ariaLabel="Tens column" />
        <Col label="1s" val={o} setter={setO} color="#10B981" ariaLabel="Ones column" />
      </div>
      <div style={{ textAlign: 'center', marginTop: 10, fontSize: 18, fontWeight: 800, color: T.text }}>Total: {total}</div>
    </div>
  );
}

// ── 5. ANGLE PROTRACTOR ─────────────────────────────────────────────────
function AngleProtractor({ config, onStateChange, disabled, flashCorrect }) {
  const snap = config?.snap ?? true;
  const snapToRaw = config?.snapTo;
  const snapTo = Array.isArray(snapToRaw) && snapToRaw.length > 0 ? snapToRaw : [0, 30, 45, 60, 90, 120, 135, 150, 180];
  const [angle, setAngle] = useState(config?.initial ?? 0);
  const svgRef = useRef(null);
  const drag = useRef(false);
  const cx = 180, cy = 180, r = 130;

  const update = (e) => {
    if (disabled) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cxE = e.clientX ?? e.touches?.[0]?.clientX;
    const cyE = e.clientY ?? e.touches?.[0]?.clientY;
    if (cxE == null) return;
    const dx = cxE - rect.left - cx;
    const dy = cyE - rect.top - cy;
    let a = -Math.atan2(dy, dx) * 180 / Math.PI;
    if (a < 0) a += 360;
    if (a > 180) a = 360 - a;
    if (snap) {
      let best = snapTo[0], bestD = Infinity;
      for (const s of snapTo) { const d = Math.abs(a - s); if (d < bestD) { best = s; bestD = d; } }
      a = best;
    } else {
      a = Math.round(a);
    }
    setAngle(a);
    onStateChange?.({ value: a });
  };

  const armX = cx + r * Math.cos(-angle * Math.PI / 180);
  const armY = cy + r * Math.sin(-angle * Math.PI / 180);

  return (
    <svg ref={svgRef} width={360} height={220}
      role="slider" aria-valuemin={0} aria-valuemax={180} aria-valuenow={angle}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        const inc = e.shiftKey ? 10 : 1;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { const v = Math.max(0, angle - inc); setAngle(v); onStateChange?.({ value: v }); }
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { const v = Math.min(180, angle + inc); setAngle(v); onStateChange?.({ value: v }); }
      }}
      style={{ display: 'block', margin: '0 auto', touchAction: 'none', cursor: disabled ? 'default' : 'grab' }}
      onPointerDown={(e) => { drag.current = true; svgRef.current.setPointerCapture?.(e.pointerId); update(e); }}
      onPointerMove={(e) => { if (drag.current) update(e); }}
      onPointerUp={(e) => { drag.current = false; svgRef.current.releasePointerCapture?.(e.pointerId); }}
      onPointerCancel={() => { drag.current = false; }}
    >
      <path d={`M ${cx - r},${cy} A ${r} ${r} 0 0 1 ${cx + r},${cy}`} fill="none" stroke={T.border} strokeWidth={2} />
      <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} stroke={T.textMid} strokeWidth={1.5} />
      {Array.from({ length: 19 }).map((_, i) => {
        const t = i * 10;
        const x1 = cx + (r - 6) * Math.cos(-t * Math.PI / 180);
        const y1 = cy + (r - 6) * Math.sin(-t * Math.PI / 180);
        const x2 = cx + r * Math.cos(-t * Math.PI / 180);
        const y2 = cy + r * Math.sin(-t * Math.PI / 180);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={T.textMid} strokeWidth={t % 30 === 0 ? 2 : 1} />;
      })}
      <line x1={cx} y1={cy} x2={armX} y2={armY} stroke={flashCorrect ? T.correct : T.accent} strokeWidth={3} strokeLinecap="round" />
      <circle cx={armX} cy={armY} r={7} fill={flashCorrect ? T.correct : T.accent} stroke="#fff" strokeWidth={2} />
      <text x={cx} y={cy + 30} textAnchor="middle" fill={T.text} fontSize={20} fontWeight={800}>{angle}°</text>
    </svg>
  );
}

// ── 6. CARTESIAN PLOTTER ────────────────────────────────────────────────
function CartesianPlotter({ config, onStateChange, disabled, flashCorrect }) {
  const xMin = config?.xMin ?? -5, xMax = config?.xMax ?? 5;
  const yMin = config?.yMin ?? -5, yMax = config?.yMax ?? 5;
  const snap = config?.snap ?? true;
  const connect = config?.connect ?? false;
  const [pts, setPts] = useState(config?.initial ?? []);
  const svgRef = useRef(null);
  const W = 360, H = 360, pad = 30;
  const xFor = (x) => pad + ((x - xMin) / (xMax - xMin)) * (W - 2 * pad);
  const yFor = (y) => pad + ((yMax - y) / (yMax - yMin)) * (H - 2 * pad);
  const screenToXY = (sx, sy) => {
    let x = xMin + ((sx - pad) / (W - 2 * pad)) * (xMax - xMin);
    let y = yMax - ((sy - pad) / (H - 2 * pad)) * (yMax - yMin);
    if (snap) { x = Math.round(x); y = Math.round(y); }
    return [x, y];
  };
  const handleClick = (e) => {
    if (disabled) return;
    const rect = svgRef.current.getBoundingClientRect();
    const [x, y] = screenToXY(e.clientX - rect.left, e.clientY - rect.top);
    if (x < xMin || x > xMax || y < yMin || y > yMax) return;
    const next = [...pts, [x, y]];
    setPts(next);
    onStateChange?.({ points: next, lastPoint: [x, y], count: next.length });
  };
  const removePoint = (i, e) => {
    if (disabled) return;
    e.stopPropagation();
    const next = pts.filter((_, j) => j !== i);
    setPts(next);
    onStateChange?.({ points: next, count: next.length });
  };
  const clear = () => {
    if (disabled) return;
    setPts([]); onStateChange?.({ points: [], count: 0 });
  };

  const gridLines = [];
  for (let x = Math.ceil(xMin); x <= xMax; x++) gridLines.push(<line key={'v' + x} x1={xFor(x)} y1={pad} x2={xFor(x)} y2={H - pad} stroke={T.border} strokeWidth={x === 0 ? 1.5 : 0.5} />);
  for (let y = Math.ceil(yMin); y <= yMax; y++) gridLines.push(<line key={'h' + y} x1={pad} y1={yFor(y)} x2={W - pad} y2={yFor(y)} stroke={T.border} strokeWidth={y === 0 ? 1.5 : 0.5} />);

  return (
    <div>
      <svg ref={svgRef} width={W} height={H} onClick={handleClick}
        role="application" aria-label="Cartesian coordinate plotter"
        style={{ display: 'block', margin: '0 auto', cursor: disabled ? 'default' : 'crosshair', background: T.surface, borderRadius: 8 }}>
        {gridLines}
        <line x1={pad} y1={yFor(0)} x2={W - pad} y2={yFor(0)} stroke={T.textMid} strokeWidth={1.5} />
        <line x1={xFor(0)} y1={pad} x2={xFor(0)} y2={H - pad} stroke={T.textMid} strokeWidth={1.5} />
        <text x={W - pad + 4} y={yFor(0) + 12} fill={T.textMid} fontSize={11}>x</text>
        <text x={xFor(0) + 4} y={pad - 4} fill={T.textMid} fontSize={11}>y</text>
        {connect && pts.length > 1 && (
          <polyline points={pts.map(([x, y]) => `${xFor(x)},${yFor(y)}`).join(' ')} fill="none" stroke={T.accent} strokeWidth={2} />
        )}
        {pts.map(([x, y], i) => (
          <g key={i}>
            <circle cx={xFor(x)} cy={yFor(y)} r={6}
              fill={flashCorrect ? T.correct : T.accent} stroke="#fff" strokeWidth={1.5}
              onClick={(e) => removePoint(i, e)}
              style={{ cursor: disabled ? 'default' : 'pointer' }} />
            <text x={xFor(x) + 8} y={yFor(y) - 8} fill={T.text} fontSize={10}>({x},{y})</text>
          </g>
        ))}
      </svg>
      <div style={{ textAlign: 'center', marginTop: 6 }}>
        <button onClick={clear} disabled={disabled} style={btnSecondary(disabled)}>Clear</button>
        <span style={{ fontSize: 11, color: T.textMid, marginLeft: 10 }}>Click to plot · click a point to remove</span>
      </div>
    </div>
  );
}

// ── 7. AREA GRID ────────────────────────────────────────────────────────
function AreaGrid({ config, onStateChange, disabled, flashCorrect }) {
  const cols = config?.cols ?? 10;
  const rows = config?.rows ?? 10;
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const down = useRef(false);

  const cell = 30;
  const inSel = (r, c) => {
    if (!start || !end) return false;
    const rMin = Math.min(start[0], end[0]), rMax = Math.max(start[0], end[0]);
    const cMin = Math.min(start[1], end[1]), cMax = Math.max(start[1], end[1]);
    return r >= rMin && r <= rMax && c >= cMin && c <= cMax;
  };

  const dims = () => {
    if (!start || !end) return { w: 0, h: 0 };
    return { w: Math.abs(start[1] - end[1]) + 1, h: Math.abs(start[0] - end[0]) + 1 };
  };
  const { w, h } = dims();
  const area = w * h, perim = w && h ? 2 * (w + h) : 0;

  useEffect(() => {
    onStateChange?.({ area, perimeter: perim, width: w, height: h, value: area });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area, perim]);

  return (
    <svg width={cols * cell + 20} height={rows * cell + 40}
      role="application" aria-label={`Area grid ${cols} by ${rows}, selected ${w} by ${h}`}
      style={{ display: 'block', margin: '0 auto', userSelect: 'none' }}
      onPointerUp={() => { down.current = false; }} onPointerLeave={() => { down.current = false; }}>
      {[...Array(rows)].map((_, r) => (
        [...Array(cols)].map((__, c) => (
          <rect key={`${r}-${c}`} x={10 + c * cell} y={10 + r * cell} width={cell - 1} height={cell - 1}
            fill={inSel(r, c) ? (flashCorrect ? T.correct : T.accent) : T.surface}
            stroke={T.border} strokeWidth={1}
            style={{ cursor: disabled ? 'default' : 'pointer' }}
            onPointerDown={() => { if (!disabled) { down.current = true; setStart([r, c]); setEnd([r, c]); } }}
            onPointerEnter={() => { if (!disabled && down.current) setEnd([r, c]); }}
          />
        ))
      ))}
      <text x={(cols * cell + 20) / 2} y={rows * cell + 32} textAnchor="middle" fill={T.text} fontSize={13} fontWeight={700}>
        {w} × {h} · Area = {area} · Perimeter = {perim}
      </text>
    </svg>
  );
}

// ── 8. PIE SLICER ───────────────────────────────────────────────────────
function PieSlicer({ config, onStateChange, disabled, flashCorrect }) {
  const slices = Math.max(2, Math.min(16, config?.slices ?? 6));
  const [lit, setLit] = useState(() => new Array(slices).fill(false));
  useEffect(() => { setLit(new Array(slices).fill(false)); }, [slices]);

  const toggle = (i) => {
    if (disabled) return;
    const next = [...lit]; next[i] = !next[i];
    setLit(next);
    const count = next.filter(Boolean).length;
    onStateChange?.({ value: count / slices, shadedCount: count, slices, fraction: count + '/' + slices });
  };

  const cx = 160, cy = 160, r = 130;
  const count = lit.filter(Boolean).length;
  const arcPath = (i) => {
    const a0 = (i / slices) * 2 * Math.PI - Math.PI / 2;
    const a1 = ((i + 1) / slices) * 2 * Math.PI - Math.PI / 2;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    return `M ${cx},${cy} L ${x0},${y0} A ${r} ${r} 0 0 1 ${x1},${y1} Z`;
  };

  return (
    <svg width={320} height={340} role="group" aria-label={`Pie with ${slices} slices, ${count} shaded`} style={{ display: 'block', margin: '0 auto' }}>
      {[...Array(slices)].map((_, i) => (
        <path key={i} d={arcPath(i)}
          fill={lit[i] ? (flashCorrect ? T.correct : T.accent) : T.surface}
          stroke={T.border} strokeWidth={1.5}
          style={{ cursor: disabled ? 'default' : 'pointer', transition: 'fill .15s' }}
          onClick={() => toggle(i)}
          role="button" tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(i); } }}
          aria-label={`Slice ${i + 1} ${lit[i] ? 'shaded' : 'unshaded'}`}
        />
      ))}
      <text x={cx} y={320} textAnchor="middle" fill={T.text} fontSize={14} fontWeight={700}>
        {count}/{slices} = {(100 * count / slices).toFixed(1)}%
      </text>
    </svg>
  );
}

// ── Shared button styles ──────────────────────────────────────────────
const btnPrimary = (disabled) => ({
  background: disabled ? T.border : T.accent, color: '#fff', border: 'none',
  borderRadius: 8, padding: '6px 12px', fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
});
const btnSecondary = (disabled) => ({
  background: T.surface, color: T.textMid, border: `1px solid ${T.border}`,
  borderRadius: 8, padding: '6px 12px',
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
});
const btnSmall = (disabled) => ({
  background: T.surface, color: T.text, border: `1px solid ${T.border}`,
  borderRadius: 6, width: 26, height: 26,
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14, opacity: disabled ? 0.5 : 1,
});
const btnSmallPrimary = (disabled) => ({
  background: disabled ? T.border : T.accent, color: '#fff', border: 'none',
  borderRadius: 6, width: 26, height: 26, cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 14, opacity: disabled ? 0.5 : 1,
});

// ══════════════════════════════════════════════════════════════════════════
// MAIN DISPATCHER
// ══════════════════════════════════════════════════════════════════════════
export default function Manipulative({
  type,
  config = {},
  prompt,
  successCondition,
  successConditionAlt,
  onCorrect,
  onWrongHints = [],
  feedbackOnStuck,
  onInteraction,
  onComplete,
  onGiveUp,
  interactiveId,   // optional — when provided, emits observer events
}) {
  const cond = successCondition || successConditionAlt;

  // Dispatcher owns attempt state. Attempts only increment on an explicit
  // Check action — exploring by clicking or dragging does NOT burn attempts.
  const [state, setState] = useState({});
  const [status, setStatus] = useState('idle'); // idle | wrong | correct | gave_up
  const [attempts, setAttempts] = useState(0);
  const [message, setMessage] = useState('');
  const [hint, setHint] = useState('');

  const handleStateChange = useCallback((newState) => {
    setState(newState);
    onInteraction?.(newState);
    if (interactiveId) {
      lumiObserver.emit({ interactiveId, type: 'interaction', payload: { state: newState, manipulativeType: type } });
    }
    // Update live hint (separate from attempt-triggered feedback).
    let matched = '';
    for (const h of onWrongHints) {
      if (evalHintCondition(h.condition, newState)) { matched = h.hint; break; }
    }
    setHint(matched);
    // If they've previously been told "not quite", a new interaction clears
    // the wrong status so the UI doesn't keep flashing red while they explore.
    if (status === 'wrong') setStatus('idle');
  }, [onInteraction, onWrongHints, status, interactiveId, type]);

  const handleCheck = () => {
    if (status === 'correct' || status === 'gave_up') return;
    if (evalSuccess(cond, state)) {
      setStatus('correct');
      setMessage(onCorrect || "Nicely done — that's right.");
      onComplete?.(state);
      if (interactiveId) {
        lumiObserver.emit({ interactiveId, type: 'completed', payload: { state, manipulativeType: type } });
      }
      return;
    }
    const next = attempts + 1;
    setAttempts(next);
    if (next >= MAX_ATTEMPTS) {
      setStatus('gave_up');
      setMessage(feedbackOnStuck || "No worries — let's come back to this one later.");
      onGiveUp?.(state);
      if (interactiveId) {
        lumiObserver.emit({ interactiveId, type: 'gave_up', payload: { state, attempts: next } });
      }
    } else {
      setStatus('wrong');
      const nudge = hint ? hint : (next === 1 ? 'Not quite — have another look.' : 'Close — check the numbers one more time.');
      setMessage(nudge);
      if (interactiveId) {
        lumiObserver.emit({ interactiveId, type: 'check_wrong', payload: { state, attempt: next, hint } });
      }
    }
  };

  const done = status === 'correct' || status === 'gave_up';
  const flashCorrect = status === 'correct';

  const common = { config, onStateChange: handleStateChange, disabled: done, flashCorrect };
  const body = (() => {
    switch (type) {
      case 'fraction_bar':       return <FractionBar {...common} />;
      case 'number_line':        return <NumberLine {...common} />;
      case 'equation_balance':   return <EquationBalance {...common} />;
      case 'place_value_blocks': return <PlaceValueBlocks {...common} />;
      case 'angle_protractor':   return <AngleProtractor {...common} />;
      case 'cartesian_plotter':  return <CartesianPlotter {...common} />;
      case 'area_grid':          return <AreaGrid {...common} />;
      case 'pie_slicer':         return <PieSlicer {...common} />;
      default: return <div style={{ color: T.wrong }}>Unknown manipulative type: {type}</div>;
    }
  })();

  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
      padding: 16, fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.text,
    }}>
      {prompt && (
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>{prompt}</div>
      )}
      {body}
      <FeedbackPanel status={status} message={message} hint={hint} attempts={attempts} />
      {!done && (
        <button onClick={handleCheck}
          style={{
            display: 'block', width: '100%', marginTop: 12, padding: '10px',
            background: T.accent, color: '#fff', border: 'none', borderRadius: 8,
            fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}>
          Check answer
        </button>
      )}
    </div>
  );
}
